import React, { useEffect, useState } from "react";
import { Grid, TextField } from "@mui/material";
import SoftBox from "components/SoftBox";
import { useAgencyInfo } from "context/agency";
import { useAppServices } from "hook/services";
import toast from "react-hot-toast";
import PrimaryButton from "../../../components/PrimaryButton";

const defaultForm = {
  plans_headline: "",
  need_help_headline: "",
  need_help_body: "",
  need_help_button1_text: "",
  need_help_button1_link: "",
  need_help_button2_text: "",
  need_help_button2_link: "",
};

function NeedHelpPlansCTA() {
  const [agency] = useAgencyInfo();
  const Service = useAppServices();
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [loadingTheme, setLoadingTheme] = useState(true);
  const [saving, setSaving] = useState(false);

  const agencyThemeDocId = () => {
    const t = agency.theme_id;
    if (!t) return null;
    if (typeof t === "object" && t._id != null) return t._id;
    return t;
  };

  const loadTheme = async () => {
    setLoadingTheme(true);
    try {
      let record = null;
      const docId = agencyThemeDocId();
      if (docId) {
        const byId = await Service.themes.get({ query: `_id=${docId}` });
        record = byId.response?.data?.[0] ?? null;
      }
      if (!record) {
        const byAgency = await Service.themes.get({
          query: `theme_type=agency&agency_id=${agency._id}`,
        });
        record = byAgency.response?.data?.[0] ?? null;
      }
      setSelectedTheme(record || null);
      const c = record?.theme_data?.content || {};
      const elements = record?.theme_data?.elements || {};
      setForm({
        plans_headline: c.plans_headline ?? elements.plans_headline ?? "",
        need_help_headline: c.need_help_headline ?? "",
        need_help_body: c.need_help_body ?? "",
        need_help_button1_text: c.need_help_button1_text ?? "",
        need_help_button1_link: c.need_help_button1_link ?? "",
        need_help_button2_text: c.need_help_button2_text ?? "",
        need_help_button2_link: c.need_help_button2_link ?? "",
      });
    } catch (e) {
      toast.error("Could not load theme");
    } finally {
      setLoadingTheme(false);
    }
  };

  useEffect(() => {
    if (agency?._id) loadTheme();
  }, [agency?._id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedTheme) {
      toast.error("No agency theme found. Configure theme in Settings first.");
      return;
    }
    setSaving(true);
    try {
      const submitData = { ...selectedTheme };
      if (submitData.theme_data?.domain) {
        submitData.theme_data = {
          ...submitData.theme_data,
          domain: { ...submitData.theme_data.domain, domain_update: false },
        };
      }
      const updatedThemeData = {
        ...submitData.theme_data,
        general: { ...submitData.theme_data?.general },
        content: {
          ...submitData.theme_data?.content,
          ...form,
        },
      };
      if (selectedTheme.theme_id == null || selectedTheme.theme_id === "") {
        toast.error(
          "This theme record has no template id. Open Settings - Configurator, save the theme once, then try again."
        );
        setSaving(false);
        return;
      }

      const payload = {
        agency_id: agency._id,
        theme_data: updatedThemeData,
        theme_type: "agency",
        theme_id: selectedTheme.theme_id,
      };
      const { response, error } = await Service.themes.updateOrCreateByAgencyId({ payload });
      if (response?.success) {
        setSelectedTheme((prev) => ({ ...prev, theme_data: updatedThemeData }));
        toast.success("Plans page settings saved");
      } else {
        toast.error(error?.message || "Save failed");
      }
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loadingTheme) {
    return (
      <SoftBox p={3}>
        <p>Loading...</p>
      </SoftBox>
    );
  }

  const fieldSx = {
    width: "100%",
    "& .MuiInputBase-root": {
      width: "100%",
    },
    "& .MuiOutlinedInput-input": {
      width: "100% !important",
      boxSizing: "border-box",
    },
  };

  return (
    <SoftBox component="form" onSubmit={handleSave}>
      <Grid container spacing={2} paddingX={3} paddingY={2} paddingBottom={4}>
        <Grid item xs={12}>
          <p style={{ color: "#67748e", fontSize: 14, marginBottom: 8 }}>
            Copy shown on the public <strong>/plans</strong> page. Leave fields empty to use default text; Need help
            buttons appear when label and link are both set, or from Contact / Phone in theme content.
          </p>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Plans page headline"
            name="plans_headline"
            value={form.plans_headline}
            onChange={handleChange}
            placeholder="Choose Your Perfect Plan From Us"
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Need help headline"
            name="need_help_headline"
            value={form.need_help_headline}
            onChange={handleChange}
            placeholder="Have questions before signing up?"
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Body text"
            name="need_help_body"
            value={form.need_help_body}
            onChange={handleChange}
            multiline
            minRows={2}
            placeholder="Contact us now for more information..."
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Button 1 text"
            name="need_help_button1_text"
            value={form.need_help_button1_text}
            onChange={handleChange}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Button 1 link"
            name="need_help_button1_link"
            value={form.need_help_button1_link}
            onChange={handleChange}
            placeholder="https:// or /contact"
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Button 2 text"
            name="need_help_button2_text"
            value={form.need_help_button2_text}
            onChange={handleChange}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Button 2 link"
            name="need_help_button2_link"
            value={form.need_help_button2_link}
            onChange={handleChange}
            placeholder="tel:+1... or https://"
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} display="flex" justifyContent="flex-end">
          <PrimaryButton type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </PrimaryButton>
        </Grid>
      </Grid>
    </SoftBox>
  );
}

export default NeedHelpPlansCTA;
